import { DateTimePicker } from '@mui/lab';
import { DateTime } from 'luxon';
import React, { useRef } from 'react';
import { useController } from 'react-hook-form';
import type { UseControllerProps } from 'react-hook-form';
import type { FieldValues } from 'react-hook-form';
import type { TFunction } from 'i18next';
import { getFieldErrors } from '../../../form';
import { TextField } from '@mui/material';
import { CustomMuiTextFieldCss } from '../../../utils/CustomMuiTextFieldCss';

export interface DateTimePickerProps<T extends FieldValues> extends UseControllerProps<T> {
   label: string;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
   t: TFunction<any>;
   disableOpenPicker?: boolean;
   getErrorPath?: (type: string) => string | undefined;
   size?: 'small' | 'medium';
   maskFormat?: string;
   disablePast?: boolean;
   maxDate?: DateTime;
   minDate?: DateTime;
   shouldDisableDate?: (day: DateTime) => boolean;
   customMuiTextField?: boolean;
   disabled?: boolean;
}

export const FormDateTimePicker = <T extends FieldValues>({
  t,
  label,
  disableOpenPicker,
  disablePast,
  getErrorPath,
  maskFormat,
  size,
  minDate,
  maxDate,
  shouldDisableDate,
  customMuiTextField,
  disabled,
  ...props
}: DateTimePickerProps<T>): JSX.Element => {
  const {
    field: { ref, ...field },
    formState,
  } = useController(props);
  const value = field.value
    ? DateTime.fromISO(field.value, {zone: 'utc'})
    : field.value ?? null;

  const inputRef = useRef<HTMLInputElement>(null);

  return (
    <DateTimePicker<DateTime>
      disablePast={disablePast ?? false}
      label={label}
      mask={maskFormat}
      disableOpenPicker={disableOpenPicker}
      renderInput={(params) => (
        <TextField
          sx={customMuiTextField ? CustomMuiTextFieldCss : {}}
          {...params}
          inputProps={{
            ...params.inputProps,
            placeholder: 'DD/MM/YYYY'
          }}
          {...getFieldErrors(field.name, formState, t, getErrorPath)}
          id={field.name}
          name={field.name}
          data-testid={field.name}
          onBlur={field.onBlur}
          ref={ref}
          size={size ?? 'medium'}
          variant={customMuiTextField ? 'filled' : 'outlined'}
        />
      )}
      {...field}
      value={value}
      disabled={
        disabled ? disabled || formState.isSubmitting : formState.isSubmitting
      }
      // Tied to the focus/blur hack
      inputRef={inputRef}
      onClose={() => {
        setTimeout(() => {
          inputRef.current?.focus();
          inputRef.current?.blur();
        }, 0);
      }}
      onChange={(date) => {
        field.onChange(date?.toISO());
      }}
    />
  );
};
